import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, ComponentSync, LL_H_C, LL_V_L, TS_Link } from '@nu-art/thunderstorm-frontend';
import { filterInstances } from '@nu-art/ts-common';
import './Tooltip_VariableInformation.scss';
import { resolveIcdCode, resolveLoincLink, resolveRxNav, resolveSnomedLink, valueType_Range, valueType_SubRange } from '@app/hcs-shared';
import { ModuleFE_Value } from '../../../../_entity/value/index.js';
import { ModuleFE_NLPData } from '../../../../_entity/nlp-data/index.js';
import { ModuleFE_MeasurementUnit } from '../../../../_entity/measurement-unit/index.js';
import { ModuleFE_Reference } from '../../../../_entity/reference/index.js';
import { Component_TagViewer } from '../../../shared-components/index.js';
export class Tooltip_VariableInformation extends ComponentSync {
    // constructor(p: Props) {
    // 	super(p);
    // 	this.logger.setMinLevel(LogLevel.Info);
    // }
    //######################### Life Cycle #########################
    deriveStateFromProps(nextProps, state) {
        const variable = nextProps.variable;
        const value = ModuleFE_Value.cache.unique(variable.valueId);
        state.nlpData = ModuleFE_NLPData.cache.unique(variable.nlpDataId);
        state.references ??= ModuleFE_Reference.cache.filter(ref => variable.referenceIds.includes(ref._id));
        state.specimen = nextProps.variable.otherProperties?.['specimen'];
        state.tags = nextProps.tagIds || variable.tagIds;
        if (value?.type.toLowerCase() === valueType_Range.toLowerCase() || value?.type.toLowerCase() === valueType_SubRange) {
            state.measurementUnit = ModuleFE_MeasurementUnit.cache.unique(value.data.unitsId);
        }
        return state;
    }
    //######################### Logic #########################
    upsertRef = async (ref) => {
        await ModuleFE_Reference.v1.upsert(ref).executeSync();
    };
    //######################### Render #########################
    renderFailedIndexAlert = (ref) => {
        return _jsxs(Button, { variant: 'text', className: 'ref-warning', onClick: () => this.upsertRef(ref), children: [_jsx("span", { className: 'warning-icon', children: "!" }), _jsx("div", { className: 'warning-message', children: "Failed to index this ID" }), _jsx("div", { className: 'hover-message', children: "Retry to index" })] });
    };
    //######################### Ref Renderers
    renderSnomedRef = (ref) => {
        let icd10 = ref.data.icd10?.split('\r\n');
        if (icd10?.length) {
            icd10 = icd10.filter(item => item.trim() !== '').map(item => item.trim());
        }
        return _jsxs(LL_V_L, { className: 'ref-item', children: [ref.creationStatus === 'failed' && this.renderFailedIndexAlert(ref), _jsxs(LL_H_C, { className: 'field', children: [_jsx("div", { className: 'field-title', children: "Snomed ID:" }), this.renderSnomedLink(ref)] }), ref.data.names?.[0]?.length && _jsxs(LL_H_C, { style: { alignItems: 'flex-start' }, className: 'field', children: [_jsx("div", { className: 'field-title', children: "Snomed Name:" }), _jsx("div", { className: 'field-data', children: `${ref.data.names?.[0]}` })] }), icd10 && _jsxs(LL_H_C, { className: 'field', children: [_jsx("div", { className: 'field-title', children: "Snomed ICD10:" }), _jsx(LL_H_C, { className: 'field-data', children: icd10.map((item, i) => _jsx("div", { className: 'field-data__item', children: item + (i === icd10.length - 1 ? '' : ',') }, `${item}-${i}`)) })] })] }, ref._id);
    };
    renderSnomedLink = (ref) => {
        return _jsx(TS_Link, { url: resolveSnomedLink(ref.data.id), target: '_blank', className: 'ref-link snomed', children: ref.data.id });
    };
    renderLoincRef = (ref) => {
        return _jsxs(LL_V_L, { className: 'ref-item', children: [ref.creationStatus === 'failed' && this.renderFailedIndexAlert(ref), _jsxs(LL_H_C, { className: 'field', children: [_jsx("div", { className: 'field-title', children: "Loinc ID:" }), this.renderLoincLink(ref)] }), _jsxs(LL_H_C, { className: 'field', children: [_jsx("div", { className: 'field-title', children: "Loinc Name:" }), _jsx("div", { className: 'field-data', children: ref.data.name })] })] }, ref._id);
    };
    renderLoincLink = (ref) => {
        return _jsx(TS_Link, { url: resolveLoincLink(ref.data.id), target: '_blank', className: 'ref-link loinc', children: ref.data.id });
    };
    renderICDLink = (ref) => {
        return _jsx(TS_Link, { url: resolveIcdCode(ref.data.id), target: '_blank', className: 'ref-link icd', children: ref.data.id });
    };
    renderRxNavLink = (refType, ref) => {
        return _jsx(TS_Link, { url: resolveRxNav(refType === 'atc' ? 'ATC' : 'RXCUI', ref.data.id), target: '_blank', className: 'ref-link rxnav', children: ref.data.id });
    };
    renderICDRef = (ref) => {
        return _jsx(LL_V_L, { className: 'ref-item', children: _jsxs(LL_H_C, { className: 'field', children: [_jsx("div", { className: 'field-title', children: "ICD code:" }), this.renderICDLink(ref)] }) }, ref._id);
    };
    renderRxNavRef = (refType, ref) => {
        return _jsx(LL_V_L, { className: 'ref-item', children: _jsxs(LL_H_C, { className: 'field', children: [_jsxs("div", { className: 'field-title', children: [refType === 'atc' ? 'ATC' : 'RxCUI', " code:"] }), this.renderRxNavLink(refType, ref)] }) }, ref._id);
    };
    renderVariableData = () => {
        const variable = this.props.variable;
        const synonyms = this.state.nlpData?.synonyms;
        const units = this.state.measurementUnit?.unitAsString.split('/').map(unit => {
            return unit.trim();
        });
        // if one of the units don't translate into a dimension, don't show dimensions.
        const dimensionsArray = units?.map((unit) => {
            return ModuleFE_MeasurementUnit.getDimensionForUnit(unit);
        });
        const hasDimensions = dimensionsArray?.reduce((result, unit) => {
            result &&= !!unit;
            return result;
        }, true);
        const dimensions = !units ? false : !hasDimensions ? 'WIP' : dimensionsArray?.join('/');
        const specimen = this.state.specimen;
        return _jsxs(LL_V_L, { className: 'variable-info-tooltip__var-data', children: [_jsx(Component_TagViewer, { tags: this.state.tags }), !!synonyms?.length && _jsxs(LL_H_C, { className: 'field', children: [_jsx("span", { className: 'field-title', children: "Synonyms:" }), _jsx("span", { className: 'field-data', children: synonyms?.join(', ') || 'N/A' })] }), variable.perspective && _jsxs(LL_H_C, { className: 'field', children: [_jsx("span", { className: 'field-title', children: "Perspective:" }), _jsx("span", { className: 'field-data', children: variable.perspective || 'N/A' })] }), units && _jsxs(LL_H_C, { className: 'field', children: [_jsx("span", { className: 'field-title', children: "Units:" }), _jsx("span", { className: 'field-data', children: units?.join('/') || 'N/A' })] }), dimensions && _jsxs(LL_H_C, { className: 'field', children: [_jsx("span", { className: 'field-title', children: "Dimensions:" }), _jsx("span", { className: 'field-data', children: dimensions || 'N/A' })] }), specimen && _jsxs(LL_H_C, { className: 'field', children: [_jsx("span", { className: 'field-title', children: "Specimen:" }), _jsx("span", { className: 'field-data', children: specimen || 'N/A' })] })] });
    };
    renderRefData = () => {
        const refs = filterInstances(this.state.references);
        if (!refs || !refs.length)
            return _jsx(LL_V_L, { className: 'variable-info-tooltip__refs-data', children: _jsx("div", { className: 'ref-item', children: _jsx("div", { className: 'ref-link', children: "No references for this variable" }) }) });
        return _jsx(LL_V_L, { className: 'variable-info-tooltip__refs-data', children: refs.map(ref => {
                const type = ref?.type;
                switch (type) {
                    case 'snomed': {
                        return this.renderSnomedRef(ref);
                    }
                    case 'loinc': {
                        return this.renderLoincRef(ref);
                    }
                    case 'icdCode': {
                        return this.renderICDRef(ref);
                    }
                    case 'rxCUI':
                        return this.renderRxNavRef('rxCUI', ref);
                    case 'atc':
                        return this.renderRxNavRef('atc', ref);
                    default:
                        return '';
                }
            }) });
    };
    render() {
        return _jsxs(_Fragment, { children: [this.renderVariableData(), this.renderRefData()] });
    }
}
//# sourceMappingURL=Tooltip_VariableInformation.js.map